/**
 * Consolidated Pipeline V2 API - Wizard-Based Evaluation
 *
 * This module implements a streamlined evaluation pipeline using a single GPT-4o
 * call with chain-of-thought prompting. Designed for the new wizard-based upload flow.
 *
 * Pipeline Flow (2 steps):
 * 1. SEQUENTIAL: Transcription (Assembly AI)
 * 2. SEQUENTIAL: Consolidated Evaluation (Single GPT-4o call)
 *
 * Benefits:
 * - 80% reduction in OpenAI API calls (1 vs 5)
 * - Faster evaluation process
 * - Simpler error handling
 * - Same data format for report compatibility
 */

import { callFirebaseFunction } from '../firebaseCalls';

/**
 * Consolidated Pipeline V2 Status Constants
 */
export const CONSOLIDATED_PIPELINE_STATUS = {
  NEW: 'NEW',
  PROCESSING_TRANSCRIPTION_V2: 'PROCESSING_TRANSCRIPTION_V2',
  TRANSCRIPTION_COMPLETE_V2: 'TRANSCRIPTION_COMPLETE_V2',
  PROCESSING_SPEAKER_ID: 'PROCESSING_SPEAKER_ID',
  SPEAKER_ID_COMPLETE: 'SPEAKER_ID_COMPLETE',
  EVALUATING_ALL_V2: 'EVALUATING_ALL_V2',
  COMPLETE_V2: 'Completed',
  FAILED_V2: 'FAILED_V2'
};

export interface PipelineExecutionLog {
  startTime: Date;
  endTime?: Date;
  totalDuration?: number;
  steps: any[];
  openaiCallsTotal: number;
  errors: any[];
  pipelineVersion: string;
  finalError?: string;
}

/**
 * Get user-friendly status label for v2 pipeline
 * @param {string} status - V2 pipeline status
 * @returns {string} User-friendly label
 */
export const getConsolidatedPipelineStatusLabel = (status: string): string => {
  const labels: Record<string, string> = {
    [CONSOLIDATED_PIPELINE_STATUS.NEW]: 'Ready to Start',
    'UPLOADING': 'Uploading Video',
    'PROCESSING_TRANSCRIPTION': 'Analyzing Video', // V1 status compatibility
    [CONSOLIDATED_PIPELINE_STATUS.PROCESSING_TRANSCRIPTION_V2]: 'Analyzing Video',
    'TRANSCRIPTION_COMPLETE': 'Video Analysis Completed', // V1 status compatibility
    [CONSOLIDATED_PIPELINE_STATUS.TRANSCRIPTION_COMPLETE_V2]: 'Video Analysis Completed',
    [CONSOLIDATED_PIPELINE_STATUS.PROCESSING_SPEAKER_ID]: 'Identifying Review Subject',
    [CONSOLIDATED_PIPELINE_STATUS.SPEAKER_ID_COMPLETE]: 'Review Subject Identified',
    [CONSOLIDATED_PIPELINE_STATUS.EVALUATING_ALL_V2]: 'Evaluating Presentation',
    [CONSOLIDATED_PIPELINE_STATUS.COMPLETE_V2]: 'Complete',
    [CONSOLIDATED_PIPELINE_STATUS.FAILED_V2]: 'Failed'
  };

  return labels[status] || status;
};

/**
 * Get progress percentage for v2 pipeline
 * @param {string} status - V2 pipeline status
 * @returns {number} Progress percentage (0-100)
 */
export const getConsolidatedPipelineProgress = (status: string): number => {
  const progressMap: Record<string, number> = {
    [CONSOLIDATED_PIPELINE_STATUS.NEW]: 0,
    'UPLOADING': 10,
    'PROCESSING_TRANSCRIPTION': 25, // V1 status compatibility
    [CONSOLIDATED_PIPELINE_STATUS.PROCESSING_TRANSCRIPTION_V2]: 25,
    [CONSOLIDATED_PIPELINE_STATUS.TRANSCRIPTION_COMPLETE_V2]: 40,
    [CONSOLIDATED_PIPELINE_STATUS.PROCESSING_SPEAKER_ID]: 50,
    [CONSOLIDATED_PIPELINE_STATUS.SPEAKER_ID_COMPLETE]: 60,
    [CONSOLIDATED_PIPELINE_STATUS.EVALUATING_ALL_V2]: 75,
    [CONSOLIDATED_PIPELINE_STATUS.COMPLETE_V2]: 100,
    [CONSOLIDATED_PIPELINE_STATUS.FAILED_V2]: 0
  };

  return progressMap[status] || 0;
};

/**
 * Execute step with retry logic
 * @param {Function} stepFunction - Function to execute
 * @param {string} stepName - Name of the step
 * @param {number} maxRetries - Maximum retry attempts
 * @param {Object} executionLog - Execution log object
 * @returns {Promise<Object>} Step result
 */
const executeStepWithRetry = async (stepFunction: () => Promise<any>, stepName: string, maxRetries: number, executionLog: PipelineExecutionLog): Promise<any> => {
  let lastError: any;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      console.log(`⚡ Executing ${stepName} (attempt ${attempt + 1}/${maxRetries + 1})...`);
      const startTime = Date.now();

      const result = await stepFunction();

      const duration = Date.now() - startTime;

      executionLog.steps.push({
        stepName,
        attempt: attempt + 1,
        duration,
        success: true,
        timestamp: new Date()
      });

      console.log(`✅ ${stepName} completed successfully (${duration}ms)`);
      return result;

    } catch (error) {
      lastError = error;
      console.error(`❌ ${stepName} failed (attempt ${attempt + 1}/${maxRetries + 1}):`, error);

      executionLog.steps.push({
        stepName,
        attempt: attempt + 1,
        error: (error as any).message,
        success: false,
        timestamp: new Date()
      });

      // If this is not the last attempt, wait before retrying
      if (attempt < maxRetries) {
        const backoffDelay = Math.min(1000 * Math.pow(2, attempt), 10000); // Exponential backoff, max 10s
        console.log(`⏳ Waiting ${backoffDelay}ms before retry...`);
        await new Promise(resolve => setTimeout(resolve, backoffDelay));
      }
    }
  }

  // All retries failed
  executionLog.errors.push({
    stepName,
    error: (lastError as any).message,
    timestamp: new Date()
  });

  throw lastError;
};

/**
 * Execute complete consolidated v2 pipeline
 * @param {string} presentationId - Presentation ID
 * @param {Object} options - Execution options
 * @param {Function} onStatusUpdate - Callback for status updates
 * @returns {Promise<Object>} Final pipeline result
 */
export const executeConsolidatedPipeline = async (
  presentationId: string,
  options: { forceReprocess?: boolean; maxRetries?: number } = {},
  onStatusUpdate: ((update: any) => void) | null = null
): Promise<any> => {
  const {
    forceReprocess = false,
    maxRetries = 3
  } = options;

  console.log('🚀 Starting Consolidated V2 Pipeline Execution:', {
    presentationId,
    forceReprocess,
    maxRetries
  });

  const executionLog: PipelineExecutionLog = {
    startTime: new Date(),
    steps: [] as any[],
    openaiCallsTotal: 0,
    errors: [] as any[],
    pipelineVersion: 'v2_consolidated'
  };

  try {
    // Step 1: Process Transcription (SEQUENTIAL)
    console.log('📝 Step 1: Processing Video Transcription...');
    if (onStatusUpdate) {
      onStatusUpdate({
        status: CONSOLIDATED_PIPELINE_STATUS.PROCESSING_TRANSCRIPTION_V2,
        message: 'Processing video transcription...',
        progress: 25
      });
    }

    const transcriptionResult = await executeStepWithRetry(
      () => processTranscriptionStepV2(presentationId, forceReprocess),
      'transcription_v2',
      maxRetries,
      executionLog
    );

    if (!transcriptionResult || !transcriptionResult.success) {
      throw new Error(`Transcription step failed: ${transcriptionResult?.error || 'Unknown error'}`);
    }

    console.log('✅ Transcription completed');
    if (onStatusUpdate) {
      onStatusUpdate({
        status: CONSOLIDATED_PIPELINE_STATUS.TRANSCRIPTION_COMPLETE_V2,
        message: 'Transcription complete',
        progress: 50
      });
    }

    // Step 2: Consolidated Evaluation (SEQUENTIAL)
    console.log('🎯 Step 2: Running Consolidated Evaluation (Single GPT-4o call)...');
    if (onStatusUpdate) {
      onStatusUpdate({
        status: CONSOLIDATED_PIPELINE_STATUS.EVALUATING_ALL_V2,
        message: 'Evaluating presentation...',
        progress: 75
      });
    }

    const evaluationResult = await executeStepWithRetry(
      () => evaluateAllCategoriesConsolidated(presentationId),
      'consolidated_evaluation',
      maxRetries,
      executionLog
    );

    if (!evaluationResult || !evaluationResult.success) {
      throw new Error(`Consolidated evaluation failed: ${evaluationResult?.error || 'Unknown error'}`);
    }

    // Track OpenAI calls
    executionLog.openaiCallsTotal = evaluationResult.openai_calls_made || 1;

    console.log('✅ Consolidated evaluation completed');
    if (onStatusUpdate) {
      onStatusUpdate({
        status: CONSOLIDATED_PIPELINE_STATUS.COMPLETE_V2,
        message: 'Evaluation complete!',
        progress: 100
      });
    }

    // Calculate total execution time
    executionLog.endTime = new Date();
    executionLog.totalDuration = executionLog.endTime.getTime() - executionLog.startTime.getTime();

    console.log('🎉 V2 Consolidated Pipeline Execution Complete:', {
      duration: executionLog.totalDuration,
      openaiCalls: executionLog.openaiCallsTotal,
      steps: executionLog.steps.length
    });

    return {
      success: true,
      presentationId,
      status: CONSOLIDATED_PIPELINE_STATUS.COMPLETE_V2,
      executionLog,
      evaluationResult
    };

  } catch (error) {
    console.error('❌ V2 Consolidated Pipeline Execution Failed:', error);

    executionLog.endTime = new Date();
    executionLog.totalDuration = executionLog.endTime.getTime() - executionLog.startTime.getTime();
    executionLog.finalError = error instanceof Error ? error.message : String(error);

    if (onStatusUpdate) {
      onStatusUpdate({
        status: CONSOLIDATED_PIPELINE_STATUS.FAILED_V2,
        message: `Evaluation failed: ${error instanceof Error ? error.message : String(error)}`,
        progress: 0,
        error: error instanceof Error ? error.message : String(error)
      });
    }

    throw error;
  }
};

/**
 * Process transcription step (V2)
 * @param {string} presentationId - Presentation ID
 * @param {boolean} forceReprocess - Force reprocessing
 * @returns {Promise<Object>} Transcription result
 */
export const processTranscriptionStepV2 = async (presentationId: string, forceReprocess: boolean = false): Promise<any> => {
  console.log('🎬 Calling processTranscriptionStepV2...', { presentationId, forceReprocess });

  try {
    const result: any = await callFirebaseFunction('processTranscriptionStepV2', {
      presentationId,
      forceReprocess
    });

    if (!result || typeof result !== 'object') {
      console.error('Invalid result from process_transcription_step_v2:', result);
      throw new Error('Invalid response from transcription service');
    }

    if (result.error) {
      throw new Error(result.error);
    }

    console.log('✅ Transcription step v2 completed:', {
      skipped: result.skipped || false,
      status: result.status,
      transcriptLength: result.transcriptData?.transcript?.length || 0
    });

    return result;
  } catch (error) {
    console.error('❌ Error in processTranscriptionStepV2:', error);
    throw error;
  }
};

/**
 * Evaluate all categories in a single consolidated call (V2)
 * @param {string} presentationId - Presentation ID
 * @returns {Promise<Object>} Consolidated evaluation result
 */
export const evaluateAllCategoriesConsolidated = async (presentationId: string): Promise<any> => {
  console.log('🎯 Calling executeV2FullPipeline...', { presentationId });

  try {
    const result: any = await callFirebaseFunction('executeV2FullPipeline', {
      presentationId
    });

    if (!result || typeof result !== 'object') {
      console.error('Invalid result from evaluate_all_categories_consolidated:', result);
      throw new Error('Invalid response from evaluation service');
    }

    if (result.error) {
      throw new Error(result.error);
    }

    console.log('✅ Consolidated evaluation completed:', {
      status: result.status,
      overallScore: result.finalReport?.overall_score,
      openaiCalls: result.openai_calls_made,
      tokensUsed: result.tokens_used
    });

    return result;
  } catch (error) {
    console.error('❌ Error in evaluateAllCategoriesConsolidated:', error);
    throw error;
  }
};

/**
 * Get current status of a presentation in the v2 pipeline
 * @param {Object} presentation - Presentation object from Firestore
 * @returns {string} Current v2 pipeline status
 */
export const getCurrentPipelineStatus = (presentation: any): string | null => {
  if (!presentation) {
    return CONSOLIDATED_PIPELINE_STATUS.NEW;
  }

  // Check if this presentation used the v2 pipeline
  if (presentation.evaluationPipelineVersion !== 'v2_consolidated') {
    return null; // Not a v2 pipeline presentation
  }

  const status = presentation.status || presentation.evaluationStatus;

  // Map to v2 status if needed
  if (Object.values(CONSOLIDATED_PIPELINE_STATUS).includes(status)) {
    return status;
  }

  return CONSOLIDATED_PIPELINE_STATUS.NEW;
};

/**
 * Check if presentation can start v2 pipeline
 * @param {Object} presentation - Presentation object
 * @returns {boolean} True if can start
 */
export const canStartConsolidatedPipeline = (presentation: any): boolean => {
  if (!presentation) {
    return false;
  }

  // Must have video uploaded
  if (!presentation.videoUrl) {
    return false;
  }

  // Check current status
  const status = presentation.status || presentation.evaluationStatus;

  // Can start if NEW or FAILED
  return status === 'NEW' ||
    status === CONSOLIDATED_PIPELINE_STATUS.NEW ||
    status === CONSOLIDATED_PIPELINE_STATUS.FAILED_V2;
};

/**
 * Estimate remaining time for v2 pipeline
 * @param {string} status - Current status
 * @returns {number} Estimated remaining time in seconds
 */
export const estimateRemainingTime = (status: string): number => {
  // Rough estimates based on typical execution times
  const estimateMap: Record<string, number> = {
    'UPLOADING': 60, // 1 minute for upload
    'PROCESSING_TRANSCRIPTION': 120, // V1 status compatibility - 2 minutes
    [CONSOLIDATED_PIPELINE_STATUS.PROCESSING_TRANSCRIPTION_V2]: 120, // 2 minutes
    [CONSOLIDATED_PIPELINE_STATUS.TRANSCRIPTION_COMPLETE_V2]: 90, // 1.5 minutes
    [CONSOLIDATED_PIPELINE_STATUS.PROCESSING_SPEAKER_ID]: 30, // 30 seconds
    [CONSOLIDATED_PIPELINE_STATUS.SPEAKER_ID_COMPLETE]: 60, // 1 minute
    [CONSOLIDATED_PIPELINE_STATUS.EVALUATING_ALL_V2]: 60, // 1 minute
    [CONSOLIDATED_PIPELINE_STATUS.COMPLETE_V2]: 0,
    [CONSOLIDATED_PIPELINE_STATUS.FAILED_V2]: 0
  };

  return estimateMap[status] || 180; // Default 3 minutes
};
